import React from 'react';
import { useMutation } from '@apollo/client';
import Alert from 'components/Alert';
import { Button } from 'components/Button';
import { useStore } from 'context/store-context';
import { GENERATE_INSTALL_LINK } from 'gql/mutations';
import { beginShopifyReauthorization } from 'utils/shopifyReauthorization';

interface ReturnsScopeWarningProps {
  hasReturnsScope?: boolean;
}

/**
 * Displays a warning banner when the store is missing the `write_returns`
 * Shopify OAuth scope. Clicking "Re-authorize ShipAid" calls the
 * generateInstallLink mutation and opens the Shopify authorization URL directly.
 */
const ReturnsScopeWarning: React.FC<ReturnsScopeWarningProps> = ({
  hasReturnsScope,
}) => {
  const { storeDomain } = useStore();

  const [generateInstallLink, { loading }] = useMutation(
    GENERATE_INSTALL_LINK,
    {
      onCompleted: (data) => {
        if (data?.generateInstallLink) {
          beginShopifyReauthorization(data.generateInstallLink);
        }
      },
    },
  );

  if (hasReturnsScope !== false) return null;

  return (
    <Alert
      message="This store needs additional Shopify permissions to process returns and exchanges. Re-authorize ShipAid to enable the supported return, exchange, and refund actions."
      type="warning"
      actions={[
        <Button
          key="reauth"
          isLoading={loading}
          onClick={() =>
            generateInstallLink({
              variables: { store: storeDomain },
            })
          }
          type="button"
        >
          Re-authorize ShipAid
        </Button>,
      ]}
    />
  );
};

export default ReturnsScopeWarning;
